import React, { useState } from 'react';
import { Mail, CheckCircle2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../redux/authentication/utils/core/axios';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Real, live capture (POST /email/newsletter/subscribe, the same
// pre-existing "early access/beta signup" table every newsletter signup on
// this site writes to -- see user_email_preferences_model.py's
// NewsletterSubscriber) tagged with `source` so this specific waitlist can
// be told apart from a general newsletter signup. Replaces a live
// checkout/subscribe action on the group-monetization pricing tiers while
// MONETIZATION_LIVE is off (see PricingPage.jsx) -- this makes zero claim
// that money can move today.
const PricingWaitlistForm = ({ source = 'pricing_creator_waitlist', className = '' }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | submitting | done | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    try {
      await apiClient.post('/email/newsletter/subscribe', {
        email,
        consent: true,
        source,
      });
      setStatus('done');
    } catch (err) {
      console.warn('Waitlist signup failed:', err);
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <div className={`flex items-center gap-2.5 text-emerald-300 text-sm font-medium ${className}`}>
        <CheckCircle2 size={18} className="flex-shrink-0" />
        {t('pricing.waitlist.success')}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`flex flex-col sm:flex-row gap-3 ${className}`} noValidate>
      <div className="flex-1 relative">
        <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
          placeholder={t('pricing.waitlist.emailPlaceholder')}
          aria-label={t('pricing.waitlist.emailPlaceholder')}
          className={`w-full pl-11 pr-4 py-3 bg-white/5 backdrop-blur-sm border rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-violet-400 transition-colors ${
            status === 'error' ? 'border-red-500' : 'border-violet-500/20'
          }`}
        />
      </div>
      <button
        type="submit"
        disabled={status === 'submitting'}
        className="px-6 py-3 bg-violet-600 hover:bg-violet-500 disabled:opacity-60 text-white rounded-lg font-semibold text-sm transition-all inline-flex items-center justify-center gap-2 whitespace-nowrap"
      >
        {status === 'submitting' ? <Loader2 size={16} className="animate-spin" /> : null}
        {t('pricing.waitlist.cta')}
      </button>
      {status === 'error' && (
        <p className="text-red-400 text-xs sm:absolute sm:mt-12">{t('pricing.waitlist.error')}</p>
      )}
    </form>
  );
};

export default PricingWaitlistForm;
